/**
 * Search API service for chat-based search functionality.
 * 
 * This module provides functions for calling the chat search API
 * and managing search sessions.
 */

import apiClient from './api';

/**
 * Perform chat-based search with natural language queries.
 * 
 * @param {string} query - Natural language search query
 * @param {string} [sessionId] - Optional session ID for conversation context
 * @param {number} [limit=10] - Maximum number of results to return
 * @param {Object} [filters] - Optional search filters
 * @returns {Promise<Object>} Search response with results
 */
export const chatSearch = async (query, sessionId = null, limit = 10, filters = null) => {
  try {
    const response = await apiClient.post('/api/search/chat', {
      query,
      session_id: sessionId,
      limit,
      filters
    });
    
    return response.data;
  } catch (error) {
    console.error('Chat search failed:', error);
    
    // Extract error message from response
    const message = error.response?.data?.detail || 'Search failed';
    throw new Error(message);
  }
};

/**
 * Create a new search session.
 * 
 * @returns {Promise<Object>} New session information
 */
export const createSearchSession = async () => {
  try {
    const response = await apiClient.post('/api/search/session', {
      action: 'create'
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to create search session:', error);
    throw new Error('Failed to create search session');
  }
};

/**
 * Clear search session history.
 * 
 * @param {string} sessionId - Session ID to clear
 * @returns {Promise<Object>} Session status after clearing
 */
export const clearSearchSession = async (sessionId) => {
  try {
    const response = await apiClient.post('/api/search/session', {
      action: 'clear',
      session_id: sessionId
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to clear search session:', error);
    throw new Error('Failed to clear search session');
  }
};

/**
 * Get search session status.
 * 
 * @param {string} sessionId - Session ID to check
 * @returns {Promise<Object>} Session status information
 */
export const getSearchSessionStatus = async (sessionId) => {
  try {
    const response = await apiClient.post('/api/search/session', {
      action: 'status',
      session_id: sessionId
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to get search session status:', error);
    throw new Error('Failed to get search session status');
  }
};

/**
 * Check search service health.
 * 
 * @returns {Promise<Object>} Service health status
 */
export const getSearchHealth = async () => {
  try {
    const response = await apiClient.get('/api/search/health');
    return response.data;
  } catch (error) {
    console.error('Failed to get search health:', error);
    throw new Error('Failed to get search health');
  }
};